<template>
  <div class="manage-projects-view">
    <div class="space-y-4">
      <!-- Entities + Task Templates side by side -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Entities Section -->
        <div class="bg-white border border-gray-200">
          <div class="bg-gray-50 border-b border-gray-200 px-4 py-2 flex justify-between items-center">
            <div class="text-sm font-medium text-gray-700">
              Entities
              <span class="text-xs text-gray-500 ml-1">({{ activeEntities.length }})</span>
            </div>
            <div v-if="isOngoing" class="flex items-center gap-2">
              <button
                v-if="selectedEntityIds.length > 0"
                @click="openBulkRemoveEntities"
                class="btn-danger-sm"
              >
                <font-awesome-icon :icon="['fas', 'trash']" class="text-xs mr-1" />
                Remove Selected ({{ selectedEntityIds.length }})
              </button>
              <EntitySearchWidget
                ref="entitySearch"
                clear-selection-on-close
                @entities-selected="onEntitiesConfirmed"
              />
            </div>
          </div>

          <!-- Inline messaging for Add Entity -->
          <div
            v-if="isOngoing && (addEntityError || addingEntity)"
            class="px-4 py-2 border-b border-gray-200 space-y-1"
          >
            <p v-if="addingEntity" class="text-xs text-gray-600">Adding entit{{ entityToAddIds.length === 1 ? 'y' : 'ies' }}...</p>
            <p v-if="addEntityError" class="text-xs text-red-600">{{ addEntityError }}</p>
          </div>

          <!-- Entity Table -->
          <div v-if="activeEntities.length === 0" class="p-6 text-center text-xs text-gray-500">
            No entities on this project yet.
            <span v-if="isOngoing">Use Add Entity to assign one.</span>
          </div>
          <div v-else class="max-h-96 overflow-y-auto">
            <table class="w-full">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th v-if="isOngoing" class="px-3 py-1.5 border-b border-gray-200 w-8">
                    <input
                      type="checkbox"
                      :checked="allEntitiesSelected"
                      :indeterminate.prop="someEntitiesSelected"
                      @change="toggleSelectAllEntities"
                      class="cursor-pointer"
                    />
                  </th>
                  <SortableTh :sort-key="'entity_name'" :sorts="entitySorts" @sort="toggleEntitySort" class="border-b border-gray-200">Entity</SortableTh>
                  <SortableTh :sort-key="'entity_type_name'" :sorts="entitySorts" @sort="toggleEntitySort" class="border-b border-gray-200 w-32">Type</SortableTh>
                  <SortableTh :sort-key="'joined_at'" :sorts="entitySorts" @sort="toggleEntitySort" class="border-b border-gray-200 w-32">Added</SortableTh>
                  <th class="px-3 py-1.5 border-b border-gray-200 w-12"></th>
                </tr>
              </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="pe in sortedActiveEntities"
                :key="pe.id"
                class="hover:bg-gray-50"
              >
                <td v-if="isOngoing" class="px-3 py-1 align-middle">
                  <input
                    type="checkbox"
                    :value="pe.entity"
                    v-model="selectedEntityIds"
                    class="cursor-pointer"
                  />
                </td>
                <td class="px-3 py-1 text-xs align-middle">
                  <router-link
                    v-if="canAccessEntity(pe.entity)"
                    :to="`/entity/${pe.entity}`"
                    class="sensitive text-neutral-900 font-semibold hover:text-neutral-700 hover:underline"
                    :data-spoof-kind="entitySpoofKind(pe.entity_type_name, pe.entity_name)"
                  >
                    {{ pe.entity_name }}
                  </router-link>
                  <span v-else class="sensitive text-gray-800" :data-spoof-kind="entitySpoofKind(pe.entity_type_name, pe.entity_name)">{{ pe.entity_name }}</span>
                </td>
                <td class="px-3 py-1 text-xs text-gray-600 align-middle">
                  {{ pe.entity_type_name || '—' }}
                </td>
                <td class="px-3 py-1 text-xs text-gray-600 align-middle">
                  {{ formatDate(pe.joined_at) }}
                </td>
                <td class="px-3 py-1 text-right align-middle">
                  <button
                    v-if="isOngoing"
                    @click="openRemoveEntity(pe)"
                    class="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors"
                    title="Remove entity from project"
                  >
                    <font-awesome-icon :icon="['fas', 'trash']" class="text-xs" />
                  </button>
                </td>
              </tr>
            </tbody>
            </table>
          </div>
        </div>

        <!-- Templates Section -->
        <div class="bg-white border border-gray-200">
          <div class="bg-gray-50 border-b border-gray-200 px-4 py-2 flex justify-between items-center">
            <div class="text-sm font-medium text-gray-700">
              Task Templates
              <span class="text-xs text-gray-500 ml-1">({{ activeTemplateCount }})</span>
            </div>
            <button
              v-if="isOngoing"
              @click="openAddTemplateModal"
              class="btn-primary-sm"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="text-xs mr-1" />
              Add Template
            </button>
          </div>

          <!-- Template Table -->
          <div v-if="activeTemplateCount === 0" class="p-6 text-center text-xs text-gray-500">
            No task templates on this project.
          </div>
          <div v-else class="max-h-96 overflow-y-auto">
            <table class="w-full">
              <thead class="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <SortableTh :sort-key="'action_name'" :sorts="templateSorts" @sort="toggleTemplateSort" class="border-b border-gray-200">Task</SortableTh>
                  <SortableTh :sort-key="'description'" :sorts="templateSorts" @sort="toggleTemplateSort" class="border-b border-gray-200">Description</SortableTh>
                  <SortableTh :sort-key="'due_days_offset'" :sorts="templateSorts" @sort="toggleTemplateSort" class="border-b border-gray-200 w-32 whitespace-nowrap">Due Offset</SortableTh>
                  <SortableTh :sort-key="'created_at'" :sorts="templateSorts" @sort="toggleTemplateSort" class="border-b border-gray-200 w-32">Added</SortableTh>
                  <th class="px-3 py-1.5 border-b border-gray-200 w-20"></th>
                </tr>
              </thead>
            <tbody class="divide-y divide-gray-100">
              <tr
                v-for="tpl in sortedActiveTemplates"
                :key="tpl.id"
                class="hover:bg-gray-50"
              >
                <td class="px-3 py-1 text-xs text-gray-900 align-middle font-medium">{{ tpl.action_name }}</td>
                <td class="px-3 py-1 text-xs text-gray-600 align-middle">{{ tpl.description || '—' }}</td>
                <td class="px-3 py-1 text-xs text-gray-600 align-middle">
                  <span v-if="tpl.due_days_offset !== null">+{{ tpl.due_days_offset }}d</span>
                  <span v-else>—</span>
                </td>
                <td class="px-3 py-1 text-xs text-gray-600 align-middle">
                  {{ formatDate(tpl.created_at) }}
                </td>
                <td class="px-3 py-1 text-right align-middle">
                  <div v-if="isOngoing" class="inline-flex items-center gap-0.5">
                    <button
                      @click="openReapplyTemplate(tpl)"
                      class="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors"
                      title="Apply this template to existing entities"
                    >
                      <font-awesome-icon :icon="['fas', 'rotate']" class="text-xs" />
                    </button>
                    <button
                      @click="openRemoveTemplate(tpl)"
                      class="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded transition-colors"
                      title="Remove template from project"
                    >
                      <font-awesome-icon :icon="['fas', 'trash']" class="text-xs" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Bulk Remove Entities Confirmation Modal -->
    <div
      v-if="bulkRemoveModal.open"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeBulkRemoveModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-800">Remove Entities from Project</h3>
        </div>
        <div class="p-4 space-y-3">
          <p class="text-sm text-gray-700">
            Remove <span class="font-medium">{{ bulkRemoveModal.entityIds.length }}</span> entit{{ bulkRemoveModal.entityIds.length === 1 ? 'y' : 'ies' }} from this project?
          </p>

          <div v-if="bulkRemoveModal.loadingImpact" class="text-xs text-gray-500">
            Checking impact...
          </div>

          <div v-else class="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
            <div>
              <span class="font-medium">{{ bulkRemoveModal.incomplete }}</span>
              incomplete action{{ bulkRemoveModal.incomplete === 1 ? '' : 's' }} across the selected entities
            </div>
            <div v-if="bulkRemoveModal.completed > 0" class="text-xs text-gray-500">
              {{ bulkRemoveModal.completed }} completed action{{ bulkRemoveModal.completed === 1 ? '' : 's' }} will be preserved either way.
            </div>
          </div>

          <p v-if="bulkRemoveModal.error" class="text-xs text-red-600">{{ bulkRemoveModal.error }}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeBulkRemoveModal"
            class="btn-secondary-sm"
            :disabled="bulkRemoveModal.submitting"
          >
            Back
          </button>
          <button
            @click="confirmBulkRemoveEntities(false)"
            class="btn-primary-sm"
            :disabled="bulkRemoveModal.submitting || bulkRemoveModal.loadingImpact"
          >
            Remove + leave actions
          </button>
          <button
            @click="confirmBulkRemoveEntities(true)"
            class="btn-primary-sm"
            :disabled="bulkRemoveModal.submitting || bulkRemoveModal.loadingImpact"
          >
            {{ bulkRemoveModal.submitting ? 'Removing...' : 'Remove + cancel incomplete actions' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Remove Entity Confirmation Modal -->
    <div
      v-if="removeModal.open"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeRemoveModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-800">Remove Entity from Project</h3>
        </div>
        <div class="p-4 space-y-3">
          <p class="text-sm text-gray-700">
            Remove <span class="sensitive font-medium" data-spoof-kind="practice">{{ removeModal.entityName }}</span> from this project?
          </p>

          <div v-if="removeModal.loadingImpact" class="text-xs text-gray-500">
            Checking impact...
          </div>

          <div v-else class="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
            <div>
              <span class="font-medium">{{ removeModal.incomplete }}</span>
              incomplete action{{ removeModal.incomplete === 1 ? '' : 's' }} for this entity
            </div>
            <div v-if="removeModal.completed > 0" class="text-xs text-gray-500">
              {{ removeModal.completed }} completed action{{ removeModal.completed === 1 ? '' : 's' }} will be preserved either way.
            </div>
          </div>

          <p v-if="removeModal.error" class="text-xs text-red-600">{{ removeModal.error }}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeRemoveModal"
            class="btn-secondary-sm"
            :disabled="removeModal.submitting"
          >
            Back
          </button>
          <button
            @click="confirmRemoveEntity(false)"
            class="btn-primary-sm"
            :disabled="removeModal.submitting || removeModal.loadingImpact"
          >
            Remove + leave actions
          </button>
          <button
            @click="confirmRemoveEntity(true)"
            class="btn-primary-sm"
            :disabled="removeModal.submitting || removeModal.loadingImpact"
          >
            {{ removeModal.submitting ? 'Removing...' : 'Remove + cancel incomplete actions' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Remove Template Confirmation Modal -->
    <div
      v-if="removeTemplateModal.open"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeRemoveTemplateModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-800">Remove Task Template</h3>
        </div>
        <div class="p-4 space-y-3">
          <p class="text-sm text-gray-700">
            Remove <span class="font-medium">{{ removeTemplateModal.templateName }}</span> from this project?
          </p>

          <div v-if="removeTemplateModal.loadingImpact" class="text-xs text-gray-500">
            Checking impact...
          </div>

          <div v-else class="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
            <div>
              <span class="font-medium">{{ removeTemplateModal.incomplete }}</span>
              incomplete action{{ removeTemplateModal.incomplete === 1 ? '' : 's' }} were spawned by this template
            </div>
            <div v-if="removeTemplateModal.completed > 0" class="text-xs text-gray-500">
              {{ removeTemplateModal.completed }} completed action{{ removeTemplateModal.completed === 1 ? '' : 's' }} will be preserved either way.
            </div>
          </div>

          <p v-if="removeTemplateModal.error" class="text-xs text-red-600">{{ removeTemplateModal.error }}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeRemoveTemplateModal"
            class="btn-secondary-sm"
            :disabled="removeTemplateModal.submitting"
          >
            Back
          </button>
          <button
            @click="confirmRemoveTemplate(false)"
            class="btn-primary-sm"
            :disabled="removeTemplateModal.submitting || removeTemplateModal.loadingImpact"
          >
            Remove + leave actions
          </button>
          <button
            @click="confirmRemoveTemplate(true)"
            class="btn-primary-sm"
            :disabled="removeTemplateModal.submitting || removeTemplateModal.loadingImpact"
          >
            {{ removeTemplateModal.submitting ? 'Removing...' : 'Remove + cancel incomplete actions' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Template Modal -->
    <div
      v-if="showAddTemplate"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeAddTemplateModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-800">Add Task Template</h3>
        </div>
        <div class="p-4 space-y-3">
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Task name</label>
            <input
              v-model="newTemplate.action_name"
              type="text"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
              placeholder="e.g. Schedule kickoff call"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Action type</label>
            <select
              v-model="newTemplate.action_type_id"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
            >
              <option :value="null">Select type...</option>
              <option v-for="type in actionTypes" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              v-model="newTemplate.description"
              rows="2"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
              placeholder="What this task is about"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-700 mb-1">Due days offset (optional)</label>
            <input
              v-model.number="newTemplate.due_days_offset"
              type="number"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400"
              placeholder="Days after entity is added"
            />
          </div>

          <div v-if="activeEntities.length > 0" class="border-t border-gray-200 pt-3 mt-3">
            <div class="flex items-center justify-between mb-1">
              <label class="block text-xs font-medium text-gray-700">
                Apply to existing entities
                <span class="text-gray-400 font-normal">({{ newTemplateEntityIds.length }} of {{ activeEntities.length }})</span>
              </label>
              <div class="flex gap-2 text-xs">
                <button @click="newTemplateEntityIds = activeEntities.map(e => e.entity)" class="text-blue-700 hover:underline">All</button>
                <button @click="newTemplateEntityIds = []" class="text-blue-700 hover:underline">None</button>
              </div>
            </div>
            <div class="max-h-40 overflow-y-auto bg-gray-50 border border-gray-200 rounded p-2 space-y-1">
              <label
                v-for="pe in activeEntities"
                :key="pe.id"
                class="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-1 py-0.5 rounded"
              >
                <input
                  type="checkbox"
                  :value="pe.entity"
                  v-model="newTemplateEntityIds"
                />
                <span class="sensitive" :data-spoof-kind="entitySpoofKind(pe.entity_type_name, pe.entity_name)">{{ pe.entity_name }}</span>
              </label>
            </div>
          </div>

          <p v-if="addTemplateError" class="text-xs text-red-600">{{ addTemplateError }}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeAddTemplateModal"
            class="btn-secondary-sm"
            :disabled="addingTemplate"
          >
            Cancel
          </button>
          <button
            @click="addTemplate"
            :disabled="!canAddTemplate || addingTemplate"
            class="btn-primary-sm"
          >
            {{ addingTemplate ? 'Adding...' : `Add Template${newTemplateEntityIds.length > 0 ? ` & Apply (${newTemplateEntityIds.length})` : ''}` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reapply Template Modal -->
    <div
      v-if="reapplyModal.open"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeReapplyModal"
    >
      <div class="bg-white rounded shadow-lg max-w-2xl w-full mx-4">
        <div class="px-4 py-3 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-800">Apply Template to Entities</h3>
        </div>
        <div class="p-4 space-y-3">
          <p class="text-sm text-gray-700">
            Apply <span class="font-medium">{{ reapplyModal.templateName }}</span> to:
          </p>

          <div v-if="activeEntities.length === 0" class="text-xs text-gray-500">
            No entities on this project to apply to.
          </div>
          <div v-else>
            <div class="flex items-center justify-between mb-1">
              <div class="text-xs text-gray-600">
                {{ reapplyModal.entityIds.length }} of {{ activeEntities.length }} selected
              </div>
              <div class="flex gap-2 text-xs">
                <button @click="reapplyModal.entityIds = activeEntities.map(e => e.entity)" class="text-blue-700 hover:underline">All</button>
                <button @click="reapplyModal.entityIds = []" class="text-blue-700 hover:underline">None</button>
              </div>
            </div>
            <div class="max-h-48 overflow-y-auto bg-gray-50 border border-gray-200 rounded p-2 space-y-1">
              <label
                v-for="pe in activeEntities"
                :key="pe.id"
                class="flex items-center gap-2 text-sm cursor-pointer hover:bg-white px-1 py-0.5 rounded"
              >
                <input
                  type="checkbox"
                  :value="pe.entity"
                  v-model="reapplyModal.entityIds"
                />
                <span class="sensitive" :data-spoof-kind="entitySpoofKind(pe.entity_type_name, pe.entity_name)">{{ pe.entity_name }}</span>
              </label>
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Entities that already have this template will be skipped automatically.
            </p>
          </div>

          <p v-if="reapplyModal.error" class="text-xs text-red-600">{{ reapplyModal.error }}</p>
          <p v-if="reapplyModal.successMessage" class="text-xs text-green-700">{{ reapplyModal.successMessage }}</p>
        </div>
        <div class="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            @click="closeReapplyModal"
            class="btn-secondary-sm"
            :disabled="reapplyModal.submitting"
          >
            {{ reapplyModal.successMessage ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-if="!reapplyModal.successMessage"
            @click="confirmReapply"
            :disabled="reapplyModal.entityIds.length === 0 || reapplyModal.submitting"
            class="btn-primary-sm"
          >
            {{ reapplyModal.submitting ? 'Applying...' : `Apply (${reapplyModal.entityIds.length})` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntitySearchWidget from '@/components/widgetcomponents/Search_Widgets/EntitySearch_V2.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import SortableTh from '@/components/Admin/MetricStudio/SortableTh.vue';
import { canAccessEntity } from '@/utils/access';
import { entitySpoofKind } from '@/utils/demoSpoof';

export default {
  name: 'ManageProjectsView',

  components: {
    FontAwesomeIcon,
    EntitySearchWidget,
    LoadingIndicator,
    SortableTh
  },

  props: {
    project: { type: Object, required: true },
    actionTypes: { type: Array, default: () => [] }
  },

  emits: ['project-updated'],

  watch: {
    activeEntities(entities) {
      const validIds = new Set(entities.map(pe => pe.entity));
      this.selectedEntityIds = this.selectedEntityIds.filter(id => validIds.has(id));
    }
  },

  data() {
    return {
      entityToAddIds: [],
      addingEntity: false,
      addEntityError: '',
      selectedEntityIds: [],
      bulkRemoveModal: {
        open: false,
        entityIds: [],
        loadingImpact: false,
        incomplete: 0,
        completed: 0,
        submitting: false,
        error: ''
      },
      entitySorts: [],
      templateSorts: [],
      showAddTemplate: false,
      newTemplate: {
        action_name: '',
        description: '',
        action_type_id: null,
        due_days_offset: null
      },
      newTemplateEntityIds: [],
      addingTemplate: false,
      addTemplateError: '',
      removeModal: {
        open: false,
        entityId: null,
        entityName: '',
        loadingImpact: false,
        incomplete: 0,
        completed: 0,
        submitting: false,
        error: ''
      },
      removeTemplateModal: {
        open: false,
        templateId: null,
        templateName: '',
        loadingImpact: false,
        incomplete: 0,
        completed: 0,
        submitting: false,
        error: ''
      },
      reapplyModal: {
        open: false,
        templateId: null,
        templateName: '',
        entityIds: [],
        submitting: false,
        error: '',
        successMessage: ''
      }
    };
  },

  computed: {
    isOngoing() {
      return this.project?.project_type === 'ONGOING';
    },

    activeEntities() {
      return (this.project?.entities || []).filter(e => !e.removed_at);
    },

    allEntitiesSelected() {
      return this.activeEntities.length > 0 && this.selectedEntityIds.length === this.activeEntities.length;
    },

    someEntitiesSelected() {
      return this.selectedEntityIds.length > 0 && this.selectedEntityIds.length < this.activeEntities.length;
    },

    activeTemplates() {
      return (this.project?.task_templates || []).filter(t => !t.removed_at);
    },

    activeTemplateCount() {
      return this.activeTemplates.length;
    },

    sortedActiveEntities() {
      const arr = this.activeEntities.slice();
      const active = this.entitySorts.length ? this.entitySorts : [{ key: 'joined_at', dir: 'desc' }];
      const valFor = (pe, key) => {
        switch (key) {
          case 'entity_name':      return (pe.entity_name || '').toLowerCase();
          case 'entity_type_name': return (pe.entity_type_name || '').toLowerCase();
          case 'joined_at':        return pe.joined_at ? new Date(pe.joined_at).getTime() : 0;
          default: return null;
        }
      };
      arr.sort((a, b) => {
        for (const { key, dir } of active) {
          const va = valFor(a, key);
          const vb = valFor(b, key);
          if (va === vb) continue;
          if (va < vb) return dir === 'asc' ? -1 : 1;
          if (va > vb) return dir === 'asc' ? 1 : -1;
        }
        return a.id - b.id;
      });
      return arr;
    },

    sortedActiveTemplates() {
      const arr = this.activeTemplates.slice();
      const active = this.templateSorts.length ? this.templateSorts : [{ key: 'order', dir: 'asc' }];
      const valFor = (tpl, key) => {
        switch (key) {
          case 'action_name':     return (tpl.action_name || '').toLowerCase();
          case 'description':     return (tpl.description || '').toLowerCase();
          case 'due_days_offset': return tpl.due_days_offset === null || tpl.due_days_offset === undefined ? Number.POSITIVE_INFINITY : tpl.due_days_offset;
          case 'created_at':      return tpl.created_at ? new Date(tpl.created_at).getTime() : 0;
          case 'order':           return tpl.order ?? 0;
          default: return null;
        }
      };
      arr.sort((a, b) => {
        for (const { key, dir } of active) {
          const va = valFor(a, key);
          const vb = valFor(b, key);
          if (va === vb) continue;
          if (va < vb) return dir === 'asc' ? -1 : 1;
          if (va > vb) return dir === 'asc' ? 1 : -1;
        }
        return a.id - b.id;
      });
      return arr;
    },

    canAddTemplate() {
      return (
        this.newTemplate.action_name.trim() !== '' &&
        this.newTemplate.action_type_id !== null
      );
    }
  },

  methods: {
    entitySpoofKind,
    canAccessEntity,

    async onEntitiesConfirmed(entityIds) {
      if (!entityIds || entityIds.length === 0) return;

      this.entityToAddIds = entityIds;
      this.addingEntity = true;
      this.addEntityError = '';

      try {
        await axios.post(`api/graph-projects/${this.project.id}/entities/`, {
          entity_ids: entityIds
        });
        this.entityToAddIds = [];
        this.$emit('project-updated');
      } catch (err) {
        this.addEntityError = err.response?.data?.error || 'Failed to add entities.';
      } finally {
        this.addingEntity = false;
      }
    },

    // ── Table sorting ────────────────────────────────────────────────

    toggleSort(stack, key) {
      // Single-column sort: clicking a different column replaces it; clicking
      // the same column cycles asc -> desc -> off.
      const current = stack[0];
      if (!current || current.key !== key) return [{ key, dir: 'asc' }];
      if (current.dir === 'asc') return [{ key, dir: 'desc' }];
      return [];
    },

    toggleEntitySort(key) {
      this.entitySorts = this.toggleSort(this.entitySorts, key);
    },

    toggleTemplateSort(key) {
      this.templateSorts = this.toggleSort(this.templateSorts, key);
    },

    async openRemoveEntity(projectEntity) {
      this.removeModal = {
        open: true,
        entityId: projectEntity.entity,
        entityName: projectEntity.entity_name,
        loadingImpact: true,
        incomplete: 0,
        completed: 0,
        submitting: false,
        error: ''
      };

      const response = await axios.get(
        `api/graph-projects/${this.project.id}/entities/${projectEntity.entity}/removal-impact/`
      );
      this.removeModal.incomplete = response.data.incomplete_count;
      this.removeModal.completed = response.data.completed_count;
      this.removeModal.loadingImpact = false;
    },

    closeRemoveModal() {
      if (this.removeModal.submitting) return;
      this.removeModal.open = false;
    },

    async confirmRemoveEntity(cancelIncomplete) {
      this.removeModal.submitting = true;
      this.removeModal.error = '';

      try {
        await axios.delete(
          `api/graph-projects/${this.project.id}/entities/${this.removeModal.entityId}/`,
          { data: { cancel_incomplete: cancelIncomplete } }
        );
        this.removeModal.open = false;
        this.$emit("project-updated");
      } catch (err) {
        this.removeModal.error = err.response?.data?.error || 'Failed to remove entity.';
      } finally {
        this.removeModal.submitting = false;
      }
    },

    toggleSelectAllEntities() {
      if (this.allEntitiesSelected) {
        this.selectedEntityIds = [];
      } else {
        this.selectedEntityIds = this.activeEntities.map(pe => pe.entity);
      }
    },

    async openBulkRemoveEntities() {
      if (this.selectedEntityIds.length === 0) return;
      this.bulkRemoveModal = {
        open: true,
        entityIds: [...this.selectedEntityIds],
        loadingImpact: true,
        incomplete: 0,
        completed: 0,
        submitting: false,
        error: ''
      };

      const response = await axios.post(
        `api/graph-projects/${this.project.id}/entities/bulk-removal-impact/`,
        { entity_ids: this.bulkRemoveModal.entityIds }
      );
      this.bulkRemoveModal.incomplete = response.data.incomplete_count;
      this.bulkRemoveModal.completed = response.data.completed_count;
      this.bulkRemoveModal.loadingImpact = false;
    },

    closeBulkRemoveModal() {
      if (this.bulkRemoveModal.submitting) return;
      this.bulkRemoveModal.open = false;
    },

    async confirmBulkRemoveEntities(cancelIncomplete) {
      this.bulkRemoveModal.submitting = true;
      this.bulkRemoveModal.error = '';

      try {
        await axios.post(
          `api/graph-projects/${this.project.id}/entities/bulk-remove/`,
          {
            entity_ids: this.bulkRemoveModal.entityIds,
            cancel_incomplete: cancelIncomplete
          }
        );
        this.bulkRemoveModal.open = false;
        this.selectedEntityIds = [];
        this.$emit('project-updated');
      } catch (err) {
        this.bulkRemoveModal.error = err.response?.data?.error || 'Failed to remove entities.';
      } finally {
        this.bulkRemoveModal.submitting = false;
      }
    },

    // ── Templates ─────────────────────────────────────────────────────

    makeBlankTemplate() {
      return {
        action_name: '',
        description: '',
        action_type_id: null,
        due_days_offset: null
      };
    },

    async loadActionTypes() {
      const response = await axios.get('api/graph-action-types/');
      this.actionTypes = response.data.results || response.data;
    },

    openAddTemplateModal() {
      // Default: apply to all current entities
      this.newTemplate = this.makeBlankTemplate();
      this.newTemplateEntityIds = this.activeEntities.map(e => e.entity);
      this.addTemplateError = '';
      this.showAddTemplate = true;
    },

    closeAddTemplateModal() {
      if (this.addingTemplate) return;
      this.showAddTemplate = false;
    },

    async addTemplate() {
      if (!this.canAddTemplate) return;
      this.addingTemplate = true;
      this.addTemplateError = '';

      try {
        await axios.post(`api/graph-projects/${this.project.id}/templates/`, {
          action_name: this.newTemplate.action_name,
          action_type_id: this.newTemplate.action_type_id,
          description: this.newTemplate.description,
          due_days_offset: this.newTemplate.due_days_offset,
          apply_to_entity_ids: this.newTemplateEntityIds
        });
        this.showAddTemplate = false;
        this.newTemplate = this.makeBlankTemplate();
        this.newTemplateEntityIds = [];
        this.$emit("project-updated");
      } catch (err) {
        this.addTemplateError = err.response?.data?.error || 'Failed to add template.';
      } finally {
        this.addingTemplate = false;
      }
    },

    async openRemoveTemplate(template) {
      this.removeTemplateModal = {
        open: true,
        templateId: template.id,
        templateName: template.action_name,
        loadingImpact: true,
        incomplete: 0,
        completed: 0,
        submitting: false,
        error: ''
      };

      const response = await axios.get(
        `api/graph-projects/${this.project.id}/templates/${template.id}/removal-impact/`
      );
      this.removeTemplateModal.incomplete = response.data.incomplete_count;
      this.removeTemplateModal.completed = response.data.completed_count;
      this.removeTemplateModal.loadingImpact = false;
    },

    closeRemoveTemplateModal() {
      if (this.removeTemplateModal.submitting) return;
      this.removeTemplateModal.open = false;
    },

    async confirmRemoveTemplate(cancelIncomplete) {
      this.removeTemplateModal.submitting = true;
      this.removeTemplateModal.error = '';

      try {
        await axios.delete(
          `api/graph-projects/${this.project.id}/templates/${this.removeTemplateModal.templateId}/`,
          { data: { cancel_incomplete: cancelIncomplete } }
        );
        this.removeTemplateModal.open = false;
        this.$emit("project-updated");
      } catch (err) {
        this.removeTemplateModal.error = err.response?.data?.error || 'Failed to remove template.';
      } finally {
        this.removeTemplateModal.submitting = false;
      }
    },

    openReapplyTemplate(template) {
      this.reapplyModal = {
        open: true,
        templateId: template.id,
        templateName: template.action_name,
        // Default to all entities checked
        entityIds: this.activeEntities.map(e => e.entity),
        submitting: false,
        error: '',
        successMessage: ''
      };
    },

    closeReapplyModal() {
      if (this.reapplyModal.submitting) return;
      this.reapplyModal.open = false;
    },

    async confirmReapply() {
      if (this.reapplyModal.entityIds.length === 0) return;
      this.reapplyModal.submitting = true;
      this.reapplyModal.error = '';
      this.reapplyModal.successMessage = '';

      try {
        const response = await axios.post(
          `api/graph-projects/${this.project.id}/templates/${this.reapplyModal.templateId}/apply/`,
          { entity_ids: this.reapplyModal.entityIds }
        );
        const { created_count, skipped_count } = response.data;
        const parts = [];
        if (created_count > 0) parts.push(`${created_count} new task${created_count === 1 ? '' : 's'} created`);
        if (skipped_count > 0) parts.push(`${skipped_count} entit${skipped_count === 1 ? 'y' : 'ies'} already had this template`);
        this.reapplyModal.successMessage = parts.length ? parts.join('; ') : 'No changes.';
        this.$emit("project-updated");
      } catch (err) {
        this.reapplyModal.error = err.response?.data?.error || 'Failed to apply template.';
      } finally {
        this.reapplyModal.submitting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  }
};
</script>

<style scoped>
.manage-projects-view {
  font-size: 12px;
}
</style>
